import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import '../../assets/saas/molecules/_bottomSheet.scss';
const BottomSheet = ({ isVisible, onClose, children, initialHeight = 50, minHeight = 0, maxHeight = 87, fitContent = true, swipeToDismiss = true }) => {
    const [currentHeight, setCurrentHeight] = useState(initialHeight);
    const [isDragging, setIsDragging] = useState(false);
    const [isVisibleContainer, setIsVisibleContainer] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [portalRoot] = useState(() => {
        let el = document.getElementById('bottom-sheet-portal-root');
        if (!el) {
            el = document.createElement('div');
            el.id = 'bottom-sheet-portal-root';
            document.body.appendChild(el);
        }
        return el;
    });
    const [contentHeight, setContentHeight] = useState(null);
    const sheetContentRef = useRef(null);
    const contentRef = useRef(null);
    const sheetBodyRef = useRef(null);
    const dragStartDataRef = useRef({ startY: 0, startHeight: 0 });
    // Measure content height and adjust sheet height
    useEffect(() => {
        if (isVisible && isReady && contentRef.current && fitContent) {
            const observer = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const contentHeightPx = entry.contentRect.height;
                    const viewportHeight = window.innerHeight;
                    const contentHeightVh = (contentHeightPx / viewportHeight) * 100;
                    // Add some padding for the drag handle and to prevent being too tight
                    const adjustedHeightVh = contentHeightVh + 8;
                    // Clamp between min and max heights
                    const clampedHeight = Math.max(minHeight, Math.min(maxHeight, adjustedHeightVh));
                    setContentHeight(clampedHeight);
                    // Only auto-adjust if not currently being dragged by the user
                    if (!isDragging) {
                        updateSheetHeight(clampedHeight);
                    }
                }
            });
            observer.observe(contentRef.current);
            return () => observer.disconnect();
        }
    }, [isVisible, isReady, fitContent, minHeight, maxHeight]);
    // Handle animation timing for opening/closing
    useEffect(() => {
        if (isVisible) {
            // Primero hacemos visible el componente
            setIsVisibleContainer(true);
            // Retrasamos ligeramente la animación para asegurar que el componente esté renderizado
            const timer = setTimeout(() => {
                setIsReady(true);
            }, 20);
            return () => clearTimeout(timer);
        }
        else {
            // Para cerrar, primero quitamos la clase de ready
            setIsReady(false);
            // Luego esperamos a que termine la animación para ocultar el componente
            const timer = setTimeout(() => {
                setIsVisibleContainer(false);
            }, 300); // Match this with CSS transition duration
            return () => clearTimeout(timer);
        }
    }, [isVisible]);
    // Update sheet height — clamp to content height when fitContent is enabled
    const updateSheetHeight = (height) => {
        const effectiveMax = (fitContent && contentHeight) ? Math.min(maxHeight, contentHeight) : maxHeight;
        const clampedHeight = Math.max(minHeight, Math.min(effectiveMax, height));
        setCurrentHeight(clampedHeight);
    };
    // Handle touch on content body — only starts drag if content fits (no scroll)
    const handleContentTouchStart = useCallback((e) => {
        if (!swipeToDismiss)
            return;
        const bodyEl = sheetBodyRef.current;
        const contentEl = contentRef.current;
        if (bodyEl && contentEl && contentEl.scrollHeight > bodyEl.clientHeight) {
            return; // content overflows — has scroll, let native scroll handle it
        }
        handleDragStart(e);
    }, [swipeToDismiss]);
    // Handle drag start
    const handleDragStart = (e) => {
        if (!swipeToDismiss)
            return;
        setIsDragging(true);
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        dragStartDataRef.current = {
            startY: clientY,
            startHeight: currentHeight
        };
        // Prevent text selection during drag
        document.body.style.userSelect = 'none';
    };
    // Handle dragging motion
    const handleDragMove = (e) => {
        if (!isDragging)
            return;
        e.preventDefault();
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        const deltaY = dragStartDataRef.current.startY - clientY;
        const heightDelta = (deltaY / window.innerHeight) * 100;
        const newHeight = dragStartDataRef.current.startHeight + heightDelta;
        updateSheetHeight(newHeight);
    };
    // Handle drag end
    const handleDragEnd = () => {
        if (!isDragging)
            return;
        setIsDragging(false);
        document.body.style.userSelect = '';
        const startHeightPosition = dragStartDataRef.current.startHeight;
        const currentDistanceDragged = startHeightPosition - currentHeight;
        const dismissThreshold = Math.min(startHeightPosition * 0.2, (80 / window.innerHeight) * 100);
        if (currentDistanceDragged > 0 && currentDistanceDragged > dismissThreshold) {
            onClose();
            return;
        }
        if (currentHeight < minHeight + 10) {
            onClose();
        }
        else if (currentHeight > maxHeight - 15) {
            // Snap to full height
            updateSheetHeight(maxHeight);
        }
        else if (contentHeight && currentHeight > contentHeight - 10 && currentHeight < contentHeight + 10) {
            // Snap to content height if we're close
            updateSheetHeight(contentHeight);
        }
        else if (Math.abs(currentHeight - initialHeight) < 10) {
            // Snap to initial height if we're close
            updateSheetHeight(initialHeight);
        }
        else {
            if (contentHeight && startHeightPosition <= contentHeight) {
                updateSheetHeight(contentHeight);
            }
            else {
                updateSheetHeight(initialHeight);
            }
        }
    };
    // Set up global event listeners for drag operations
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleDragMove);
            document.addEventListener('mouseup', handleDragEnd);
            document.addEventListener('touchmove', handleDragMove, { passive: false });
            document.addEventListener('touchend', handleDragEnd);
            return () => {
                document.removeEventListener('mousemove', handleDragMove);
                document.removeEventListener('mouseup', handleDragEnd);
                document.removeEventListener('touchmove', handleDragMove);
                document.removeEventListener('touchend', handleDragEnd);
            };
        }
    }, [isDragging, currentHeight, contentHeight]);
    // Handle body scroll lock when modal is open
    useEffect(() => {
        if (isVisible) {
            document.body.style.overflow = 'hidden';
            document.body.classList.add('bottom-sheet-open');
            // No actualizamos la altura aquí, dejaremos que el ResizeObserver lo haga
        }
        else {
            document.body.style.overflow = '';
            document.body.classList.remove('bottom-sheet-open');
        }
        return () => {
            document.body.style.overflow = '';
            document.body.classList.remove('bottom-sheet-open');
        };
    }, [isVisible]);
    if (!isVisibleContainer)
        return null;
    const isFullscreen = currentHeight >= maxHeight - 5;
    const bottomSheetContent = (_jsxs("div", { className: `bottom-sheet-overlay ${isReady ? 'bottom-sheet-overlay--visible' : 'bottom-sheet-overlay--hidden'}`, children: [_jsx("div", { className: `bottom-sheet-overlay__backdrop ${isReady ? 'backdrop--visible' : 'backdrop--hidden'}`, onClick: onClose }), _jsxs("div", { ref: sheetContentRef, className: `
          bottom-sheet-content 
          ${isDragging ? 'bottom-sheet-content--dragging' : ''}
          ${isFullscreen ? 'bottom-sheet-content--fullscreen' : ''}
          ${isReady ? 'bottom-sheet-content--visible' : 'bottom-sheet-content--hidden'}
          ${fitContent ? 'bottom-sheet-content--fit-content' : ''}
        `, style: { height: `${currentHeight}svh` }, children: [_jsx("div", { className: "bottom-sheet-header", onMouseDown: swipeToDismiss ? handleDragStart : undefined, onTouchStart: swipeToDismiss ? handleDragStart : undefined, children: swipeToDismiss && _jsx("span", { className: "bottom-sheet-header__drag-indicator" }) }), _jsx("div", { className: "bottom-sheet-body", ref: sheetBodyRef, onTouchStart: swipeToDismiss ? handleContentTouchStart : undefined, children: _jsx("div", { ref: contentRef, className: "bottom-sheet-body__content", children: children }) })] })] }));
    return portalRoot ? ReactDOM.createPortal(bottomSheetContent, portalRoot) : null;
};
export default BottomSheet;
